import { useRoles } from '../../hooks/useRoles';
import { useRoleCategories } from '../../hooks/useRoleCategories';

/**
 * The picker's working value. null means "the other side is selected";
 * an empty string means "this side is selected but nothing picked yet",
 * so the toggle knows which select to show before a pick is made.
 */
export interface TemplateTarget {
  role_id: string | null;
  category_id: string | null;
}

export function targetIsComplete(t: TemplateTarget): boolean {
  return !!t.role_id || !!t.category_id;
}

/**
 * Choose what a kit / onboarding template applies to: one specific role, or
 * a whole role category (the shared baseline for every role in the bucket).
 * Exactly one of the two; switching mode clears the other side.
 */
export function TemplateTargetPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: TemplateTarget;
  onChange: (target: TemplateTarget) => void;
  disabled?: boolean;
}) {
  const roles = useRoles();
  const categories = useRoleCategories();

  const mode: 'role' | 'category' = value.category_id !== null ? 'category' : 'role';

  function setMode(next: 'role' | 'category') {
    if (next === mode) return;
    onChange(
      next === 'role' ? { role_id: '', category_id: null } : { role_id: null, category_id: '' },
    );
  }

  const toggleClass = (active: boolean) =>
    [
      'rounded-token-sm px-3 py-1.5 text-sm font-medium transition-colors',
      active ? 'bg-accent text-fg-on-accent' : 'bg-surface-2 text-fg-muted hover:text-fg',
    ].join(' ');

  return (
    <div className="space-y-2">
      <span className="block text-sm font-medium text-fg-muted">Applies to</span>
      <div className="flex gap-1" role="group" aria-label="Template target type">
        <button
          type="button"
          className={toggleClass(mode === 'role')}
          onClick={() => setMode('role')}
          disabled={disabled}
        >
          One role
        </button>
        <button
          type="button"
          className={toggleClass(mode === 'category')}
          onClick={() => setMode('category')}
          disabled={disabled}
        >
          Role category
        </button>
      </div>

      {mode === 'role' ? (
        <select
          value={value.role_id ?? ''}
          onChange={(e) => onChange({ role_id: e.target.value, category_id: null })}
          disabled={disabled}
          aria-label="Role"
          className="block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm"
        >
          <option value="">Choose a role…</option>
          {(roles.data ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
              {r.category_name ? ` (${r.category_name})` : ''}
            </option>
          ))}
        </select>
      ) : (
        <select
          value={value.category_id ?? ''}
          onChange={(e) => onChange({ role_id: null, category_id: e.target.value })}
          disabled={disabled}
          aria-label="Role category"
          className="block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm"
        >
          <option value="">Choose a category…</option>
          {(categories.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      )}

      <span className="block text-xs text-fg-dim">
        {mode === 'role'
          ? 'Applies to people with this exact role. Where it clashes with their category template, this one wins.'
          : 'The shared baseline for every role in this category. A role’s own template overrides it item by item.'}
      </span>
    </div>
  );
}
